import { useEffect, useState } from 'react';
import { Send, Loader2, MessageCircle, Mail } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { invokeWithRealError } from '@/lib/invokeWithRealError';
import { toast } from '@/hooks/use-toast';
import { formatarCNPJ, formatarTelefone, keepOnlyDigits } from '@/utils/formatters';

interface Props {
  solicitacaoId: string;
  cnpj: string | null;
  razaoSocial: string | null;
  competencia: string;
}

interface UltimosEnvios {
  email: { enviado_em: string | null; email_destino: string | null; status: string } | null;
  whatsapp: { created_at: string; telefone: string | null; status: string | null } | null;
}

type Canal = 'whatsapp' | 'email';

function dataHora(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

export function EnviarFaturaPainel({ solicitacaoId, cnpj, whatsappInicial, emailInicial }: {
  solicitacaoId: string;
  cnpj: string | null;
  whatsappInicial?: string;
  emailInicial?: string;
}) {
  const [whatsapp, setWhatsapp] = useState(whatsappInicial || '');
  const [email, setEmail] = useState(emailInicial || '');
  const [enviando, setEnviando] = useState<Canal | null>(null);
  const [ultimos, setUltimos] = useState<UltimosEnvios | null>(null);

  const carregarUltimos = async () => {
    const { data } = await invokeWithRealError('enviar-fatura-ultimos-envios', { body: { solicitacao_id: solicitacaoId } });
    if (data?.ok) setUltimos({ email: data.email, whatsapp: data.whatsapp });
  };

  useEffect(() => {
    if (whatsappInicial) setWhatsapp((a) => a || whatsappInicial);
    if (emailInicial) setEmail((a) => a || emailInicial);
  }, [whatsappInicial, emailInicial]);

  useEffect(() => {
    const cnpjDigits = keepOnlyDigits(cnpj || '');
    let cancelado = false;
    carregarUltimos();
    if (cnpjDigits.length === 14) {
      (async () => {
        const { data } = await supabase
          .from('cnpj_contato_master')
          .select('contato_real_email, contato_real_telefone')
          .eq('cnpj', cnpjDigits)
          .maybeSingle();
        if (cancelado) return;
        if (data?.contato_real_email) setEmail((a) => a || data.contato_real_email || '');
        const tel = keepOnlyDigits(data?.contato_real_telefone || '');
        if (tel.length === 11) setWhatsapp((a) => a || tel);
      })();
    }
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cnpj, solicitacaoId]);
  const whatsDigits = keepOnlyDigits(whatsapp);
  const whatsOk = whatsDigits.length === 11;
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const enviar = async (canal: Canal) => {
    setEnviando(canal);
    try {
      const { data, error, mensagem } = await invokeWithRealError('enviar-fatura-para-cliente', {
        body: {
          solicitacao_id: solicitacaoId,
          whatsapp: canal === 'whatsapp' ? whatsDigits : undefined,
          email: canal === 'email' ? email.trim() : undefined,
        },
      });
      if (error) throw new Error(mensagem || 'Erro ao enviar');
      if (!data?.ok) throw new Error(data?.mensagem || data?.error || 'Não foi possível enviar');
      toast({ title: canal === 'whatsapp' ? 'WhatsApp' : 'E-mail', description: data.mensagem });
      carregarUltimos();
    } catch (e) {
      toast({ title: 'Não enviou', description: e instanceof Error ? e.message : 'Erro ao enviar', variant: 'destructive' });
    } finally {
      setEnviando(null);
    }
  };

  const emailJaFoi = ultimos?.email?.status === 'enviado' && ultimos.email.enviado_em;
  const whatsJaFoi = !!ultimos?.whatsapp;

  return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="enviar-whats">WhatsApp do cliente</Label>
              <div className="flex gap-2">
                <Input id="enviar-whats" placeholder="(51) 98877-6655" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} />
                <Button
                  className="shrink-0 bg-success hover:bg-success/90 text-white"
                  onClick={() => enviar('whatsapp')}
                  disabled={!whatsOk || !!enviando}
                >
                  {enviando === 'whatsapp' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <MessageCircle className="mr-1.5 h-4 w-4" />}
                  {whatsJaFoi ? 'Reenviar WhatsApp' : 'Enviar WhatsApp'}
                </Button>
              </div>
              {whatsapp && !whatsOk ? (
                <p className="text-xs text-warning">Informe 11 dígitos com DDD.</p>
              ) : whatsJaFoi ? (
                <p className="text-xs text-muted-foreground">
                  Último envio: {dataHora(ultimos!.whatsapp!.created_at)}
                  {ultimos!.whatsapp!.telefone ? ` para ${formatarTelefone(keepOnlyDigits(ultimos!.whatsapp!.telefone).slice(-11))}` : ''}.
                  {' '}Pode reenviar se o cliente não recebeu.
                </p>
              ) : ultimos && (
                <p className="text-xs text-muted-foreground">Esta fatura ainda não foi enviada por WhatsApp.</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="enviar-email">E-mail do cliente</Label>
              <div className="flex gap-2">
                <Input id="enviar-email" type="email" placeholder="financeiro@empresa.com.br" value={email} onChange={(e) => setEmail(e.target.value)} />
                <Button className="shrink-0" onClick={() => enviar('email')} disabled={!emailOk || !!enviando}>
                  {enviando === 'email' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Mail className="mr-1.5 h-4 w-4" />}
                  {emailJaFoi ? 'Reenviar e-mail' : 'Enviar e-mail'}
                </Button>
              </div>
              {email.trim() && !emailOk ? (
                <p className="text-xs text-warning">E-mail incompleto.</p>
              ) : emailJaFoi ? (
                <p className="text-xs text-muted-foreground">
                  Último envio: {dataHora(ultimos!.email!.enviado_em)} para {ultimos!.email!.email_destino}.
                  {' '}Pode reenviar se o cliente não recebeu.
                </p>
              ) : ultimos && (
                <p className="text-xs text-muted-foreground">Esta fatura ainda não foi enviada por e-mail.</p>
              )}
            </div>

            <p className="text-xs text-muted-foreground">
              WhatsApp leva o PDF; o e-mail sai no padrão de sempre (análise + PDF, assinado pelo dono do cliente).
              O número e o e-mail usados ficam salvos no contato do cliente.
            </p>
          </div>
  );
}

export function EnviarFaturaClienteButton({ solicitacaoId, cnpj, razaoSocial, competencia }: Props) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        className="inline-flex items-center gap-0.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold bg-primary/10 text-primary border-primary/30 hover:bg-primary/20 transition-colors cursor-pointer whitespace-nowrap"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title="Enviar esta fatura ao cliente por WhatsApp ou e-mail"
      >
        <Send className="h-3 w-3" />
        Enviar
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[480px]" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Enviar fatura ao cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-muted rounded-lg p-3">
              <p className="font-semibold text-sm">{razaoSocial || 'Cliente'}</p>
              <p className="text-xs text-muted-foreground">
                CNPJ: {cnpj ? formatarCNPJ(cnpj) : '-'} · Fatura {competencia}
              </p>
            </div>
            {open && <EnviarFaturaPainel solicitacaoId={solicitacaoId} cnpj={cnpj} />}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
